import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Employee, EmployeeEvaluation } from "@/types/employee";
import EvaluationForm from "@/components/EvaluationForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const EvaluatePage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { profile, loading: authLoading } = useAuth();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [initialEvaluation, setInitialEvaluation] = useState<EmployeeEvaluation | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!employeeId) {
        setLoading(false);
        return;
      }

      // Sicherheits-/UX-Guard: Niemand kann sich selbst bewerten (auch Admin/HR nicht)
      if (!authLoading && profile?.employee_id && profile.employee_id === employeeId) {
        toast.error("Sie können keine Bewertung für sich selbst erstellen oder bearbeiten.");
        navigate(`/employee/${employeeId}`, { replace: true });
        return;
      }

      // Load employee from backend
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('id, name, position, employee_number, created_at')
        .eq('id', employeeId)
        .single();

      if (employeeError || !employeeData) {
        console.error('Employee not found:', employeeError);
        navigate('/employees');
        return;
      }

      setEmployee({
        id: employeeData.id,
        name: employeeData.name,
        position: employeeData.position || '',
        employeeNumber: employeeData.employee_number || '',
        startDate: employeeData.created_at?.split('T')[0] || ''
      });

      if (editId) {
        try {
          const { data: evalData, error: evalError } = await supabase.from('evaluations').select('*').eq('id', editId).single();
          if (evalError) throw evalError;

          const { data: ratingsData } = await supabase.from('criteria_ratings').select('*').eq('evaluation_id', editId);
          const criteriaWithComments: any = {};
          for (const rating of ratingsData || []) {
            const { data: commentsData } = await supabase.from('rating_comments').select(`id, text, created_at, profiles!inner(full_name)`).eq('rating_id', rating.id).order('created_at', { ascending: true });
            criteriaWithComments[rating.criteria_key] = {
              rating: rating.rating,
              points: rating.points,
              sliderValue: rating.points,
              comments: (commentsData || []).map(c => ({ text: c.text, date: c.created_at, userId: '', userName: c.profiles.full_name }))
            };
          }
          setInitialEvaluation({ id: evalData.id, employeeId: evalData.employee_id, month: evalData.month, date: evalData.date, criteria: criteriaWithComments, totalScore: Number(evalData.total_score), maxScore: evalData.max_score, performanceLevel: evalData.performance_level });
        } catch (error) {
          console.error('Error loading evaluation:', error);
          toast.error('Fehler beim Laden der Bewertung');
        }
      }
      setLoading(false);
    };
    loadData();
  }, [employeeId, editId, navigate, authLoading, profile?.employee_id]);

  const handleSave = async (evaluation: Omit<EmployeeEvaluation, 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.error("Sie müssen angemeldet sein"); return; }

      if (editId) {
        await supabase.from('evaluations').update({ month: evaluation.month, date: evaluation.date, total_score: evaluation.totalScore, max_score: evaluation.maxScore, performance_level: evaluation.performanceLevel }).eq('id', editId);
        await supabase.from('criteria_ratings').delete().eq('evaluation_id', editId);
        const criteriaEntries = Object.entries(evaluation.criteria).map(([criteriaKey, rating]) => ({
          evaluation_id: editId,
          criteria_key: criteriaKey,
          rating: rating.rating,
          points: (rating as any).sliderValue ?? rating.points
        }));
        await supabase.from('criteria_ratings').insert(criteriaEntries);
        for (const [criteriaKey, rating] of Object.entries(evaluation.criteria)) {
          if (rating.comments && rating.comments.length > 0) {
            const ratingData = await supabase.from('criteria_ratings').select('id').eq('evaluation_id', editId).eq('criteria_key', criteriaKey).single();
            if (ratingData.data) {
              await supabase.from('rating_comments').insert(rating.comments.map(comment => ({ rating_id: ratingData.data.id, text: comment.text, created_by: user.id })));
            }
          }
        }
        toast.success("Bewertung erfolgreich aktualisiert!");
      } else {
        const { data: evalData, error: evalError } = await supabase.from('evaluations').insert({ employee_id: evaluation.employeeId, month: evaluation.month, date: evaluation.date, total_score: evaluation.totalScore, max_score: evaluation.maxScore, performance_level: evaluation.performanceLevel, created_by: user.id }).select().single();
        if (evalError) throw evalError;
        const criteriaEntries = Object.entries(evaluation.criteria).map(([criteriaKey, rating]) => ({
          evaluation_id: evalData.id,
          criteria_key: criteriaKey,
          rating: rating.rating,
          points: (rating as any).sliderValue ?? rating.points
        }));
        await supabase.from('criteria_ratings').insert(criteriaEntries);
        for (const [criteriaKey, rating] of Object.entries(evaluation.criteria)) {
          if (rating.comments && rating.comments.length > 0) {
            const ratingData = await supabase.from('criteria_ratings').select('id').eq('evaluation_id', evalData.id).eq('criteria_key', criteriaKey).single();
            if (ratingData.data) {
              await supabase.from('rating_comments').insert(rating.comments.map(comment => ({ rating_id: ratingData.data.id, text: comment.text, created_by: user.id })));
            }
          }
        }
        toast.success("Bewertung erfolgreich gespeichert!");
      }
    } catch (error) {
      console.error('Error saving evaluation:', error);
      toast.error("Fehler beim Speichern der Bewertung");
    }
  };

  if (loading || !employee) {
    return <div className="flex items-center justify-center h-[calc(100vh-4rem)]"><p>Laden...</p></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <EvaluationForm employee={employee} onSave={handleSave} initialData={initialEvaluation} />
    </div>
  );
};

export default EvaluatePage;
